import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";

import { apiService } from "../../config/api";
import { useSecureAuth } from "../../contexts/SecureAuthContext";

import PortfolioSummaryBar from "./PortfolioSummaryBar";
import StudyCardGrid from "./StudyCardGrid";
import { StudyCardData, HealthStatus } from "./StudyCard";
import StudyFilters, { HealthFilter, StudyStatusFilter } from "./StudyFilters";
import StudyDetailDrawer from "./StudyDetailDrawer";


const API_ROOT =
  (window as any).__ENV__?.API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:3002';

const API_BASE = `${API_ROOT.replace(/\/$/, '')}/api/analytics/study-mgmt`;


// --------------------------- Types ---------------------------

interface PortfolioSummary {
  total_studies: number;
  studies_by_status: Record<string, number>;
  total_participants: {
    invited: number;
    accepted: number;
    connected: number;
    currently_active: number;
    completed: number;
    withdrawn: number;
    expired: number;
    total: number;
  };
  studies_needing_attention: number;
  recent_activity: { type: string; study_name: string; participant_id?: string; timestamp: string }[];
}

interface StudiesListResponse {
  page: number;
  limit: number;
  total: number;
  items: StudyCardData[];
}

interface StudyDetailResponse {
  meta: {
    study_id: number;
    study_code: string;
    study_name: string | null;
    researcher_email: string | null;
    status: string;
    study_type: string;
    study_created_at: string | null;
    study_setup_completed_at: string | null;
    first_participant_setup_at: string | null;
    status_changed_at: string | null;
    status_change_reason: string | null;
    last_activity_at: string | null;
    enrollment_target: number | null;
    enrollment_deadline: string | null;
  };
  counts: {
    invited: number;
    accepted: number;
    connected: number;
    currently_active: number;
    completed: number;
    withdrawn: number;
    expired: number;
    total: number;
    enrolled: number;
  };
  rates: { acceptance_rate: number | null; connection_rate: number | null };
  status_history?: { old_status: string | null; new_status: string; changed_at: string; changed_by: string | null }[];
  participants?: { anonymized_id: string; status: string; invited_at?: string | null; accepted_at?: string | null; connected_at?: string | null }[];
}

// --------------------------- Helpers ---------------------------

const fmt = {
  num: (n: number | null | undefined) => typeof n === "number" ? n.toLocaleString() : "—",
  date: (s?: string | null) => s ? new Date(s).toLocaleString() : "—",
};

async function buildAuthHeaders(): Promise<HeadersInit> {
  const token = await apiService.getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson<T = any>(url: string, _user: any, opts: RequestInit & { headers?: HeadersInit } = {}): Promise<T> {
  let headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
    ...(await buildAuthHeaders()),
  };

  let res = await fetch(url, { method: "GET", credentials: "include", headers, ...opts });
  let text = await res.text();

  if (res.status === 401) {
    headers = { ...headers, ...(await buildAuthHeaders()) };
    res = await fetch(url, { method: "GET", credentials: "include", headers, ...opts });
    text = await res.text();
  }

  let data: any;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    const snippet = text?.slice(0, 120) + (text && text.length > 120 ? "…" : "");
    throw new Error(!res.ok ? `HTTP ${res.status} ${res.statusText} – expected JSON but got "${snippet}"` : "Unexpected non-JSON response");
  }

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${data?.error || data?.message || res.statusText}`);
  }
  return data as T;
}

// Sort order for health status (needs-attention first)
const HEALTH_ORDER: Record<HealthStatus, number> = { "needs-attention": 0, "monitor": 1, "on-track": 2, "no-data": 3 };

// --------------------------- Component ---------------------------

const StudyMgmtDashboard: React.FC = () => {
  const { user } = useSecureAuth();

  // Portfolio summary
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [portfolioLoading, setPortfolioLoading] = useState(true);

  // Studies list
  const [allStudies, setAllStudies] = useState<StudyCardData[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Filters
  const [healthFilter, setHealthFilter] = useState<HealthFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StudyStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [showTestStudies, setShowTestStudies] = useState(false);
  const [showTableView, setShowTableView] = useState(false);

  // Detail drawer
  const [selectedStudy, setSelectedStudy] = useState<StudyCardData | null>(null);
  const [detail, setDetail] = useState<StudyDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initRef = useRef(false);

  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const data = await fetchJson<PortfolioSummary>(`${API_BASE}/portfolio-summary`, user);
      setPortfolio(data);
    } catch {
      setPortfolio(null);
    } finally {
      setPortfolioLoading(false);
    }
  }, [user]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    const qs = new URLSearchParams();
    qs.set("type", showTestStudies ? "all" : "production");
    qs.set("sort", "urgency");
    qs.set("limit", "200");
    if (statusFilter !== "all") qs.set("status", statusFilter);

    try {
      const data = await fetchJson<StudiesListResponse>(`${API_BASE}/studies-list?${qs.toString()}`, user);
      setAllStudies(data.items || []);
    } catch (e: any) {
      setAllStudies([]);
      setListError(e?.message || "Failed to load studies");
    } finally {
      setListLoading(false);
    }
  }, [user, showTestStudies, statusFilter]);

  const loadDetail = useCallback(async (study: StudyCardData) => {
    setSelectedStudy(study);
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await fetchJson<StudyDetailResponse>(
        `${API_BASE}/study-detail/${study.study_id}?includeParticipants=true`,
        user
      );
      setDetail(data);
    } catch (e: any) {
      setDetailError(e?.message || "Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  }, [user]);

  const exportCsv = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      qs.set("type", showTestStudies ? "all" : "production");
      qs.set("limit", "2000");
      const headers = await buildAuthHeaders();
      const res = await fetch(`${API_BASE}/studies-csv?${qs.toString()}`, { method: "GET", credentials: "include", headers });
      if (!res.ok) throw new Error(`CSV failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "studies.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.message || "CSV export failed");
    }
  }, [showTestStudies]);

  // Init
  useEffect(() => {
    if (!user || initRef.current) return;
    initRef.current = true;
    loadPortfolio();
    loadList();
  }, [user, loadPortfolio, loadList]);

  // Reload when filters change
  useEffect(() => {
    if (!initRef.current) return;
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => loadList(), 300);
    return () => { if (searchRef.current) clearTimeout(searchRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTestStudies, statusFilter]);

  // Client-side filtering (search + health filter applied to loaded data)
  const filteredStudies = useMemo(() => {
    let items = allStudies;

    if (healthFilter !== "all") {
      items = items.filter(s => s.health_status === healthFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(s =>
        (s.study_name || "").toLowerCase().includes(q) ||
        s.study_code.toLowerCase().includes(q) ||
        (s.researcher_email || "").toLowerCase().includes(q)
      );
    }

    // Sort by urgency
    return [...items].sort((a, b) => {
      const oa = HEALTH_ORDER[a.health_status] ?? 9;
      const ob = HEALTH_ORDER[b.health_status] ?? 9;
      return oa !== ob ? oa - ob : new Date(b.last_activity_at || 0).getTime() - new Date(a.last_activity_at || 0).getTime();
    });
  }, [allStudies, healthFilter, search]);

  // Derived summary counts
  const summaryData = useMemo(() => {
    const p = portfolio?.total_participants;
    const onTrack = allStudies.filter(s => s.health_status === "on-track").length;
    const needsAttention = allStudies.filter(s => s.health_status === "needs-attention").length;
    return {
      totalStudies: portfolio?.studies_by_status?.active || allStudies.length,
      onTrack,
      needsAttention,
      totalEnrolled: p?.total || 0,
      currentlyActive: p?.currently_active || 0,
    };
  }, [portfolio, allStudies]);

  // --------------------------- UI ---------------------------

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: "calc(100vh - 64px)", // account for app bar
        display: "flex",
        flexDirection: "column",
        py: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={700}>
          Study Portfolio
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => { loadPortfolio(); loadList(); }}>
            Refresh
          </Button>
          <Button size="small" variant="contained" onClick={exportCsv}>
            CSV
          </Button>
        </Stack>
      </Stack>

      {/* Zone 1: Summary Bar */}
      <Box sx={{ flexShrink: 0 }}>
        <PortfolioSummaryBar
          totalStudies={summaryData.totalStudies}
          onTrack={summaryData.onTrack}
          needsAttention={summaryData.needsAttention}
          totalEnrolled={summaryData.totalEnrolled}
          currentlyActive={summaryData.currentlyActive}
          loading={portfolioLoading}
        />
      </Box>

      {/* Zone 3 (top): Filters */}
      <Box sx={{ flexShrink: 0 }}>
        <StudyFilters
          healthFilter={healthFilter}
          statusFilter={statusFilter}
          search={search}
          showTestStudies={showTestStudies}
          showTableView={showTableView}
          onHealthFilterChange={setHealthFilter}
          onStatusFilterChange={(f) => { setStatusFilter(f); }}
          onSearchChange={setSearch}
          onShowTestStudiesChange={setShowTestStudies}
          onShowTableViewChange={setShowTableView}
        />
      </Box>

      {/* Loading indicator */}
      {listLoading && <LinearProgress sx={{ flexShrink: 0, mb: 1 }} />}
      {listError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{listError}</Typography>}

      {/* Zone 2: Card Grid or Table View */}
      {showTableView ? (
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <TableView studies={filteredStudies} onView={loadDetail} />
        </Box>
      ) : (
        <StudyCardGrid
          studies={filteredStudies}
          onCardClick={loadDetail}
          loading={listLoading}
        />
      )}

      {/* Detail Drawer */}
      <StudyDetailDrawer
        open={!!selectedStudy}
        onClose={() => { setSelectedStudy(null); setDetail(null); }}
        study={selectedStudy}
        detail={detail}
        loading={detailLoading}
        error={detailError}
      />
    </Container>
  );
};

// Preserved table view for power users
function TableView({ studies, onView }: { studies: StudyCardData[]; onView: (s: StudyCardData) => void }) {
  return (
    <Card>
      <CardHeader
        title={`${studies.length} studies`}
        titleTypographyProps={{ variant: "subtitle2" }}
      />
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead>
              <tr>
                {["Study", "PI", "Status", "Health", "Invited", "Enrolled", "Active", "Last Activity", ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {studies.map(row => (
                <tr key={row.study_id} style={{ borderBottom: "1px solid #f2f2f2" }}>
                  <td style={{ padding: "8px" }}>
                    <div style={{ fontWeight: 600 }}>{row.study_name || "(untitled)"}</div>
                    <div style={{ color: "#666", fontSize: "0.7rem" }}>{row.study_code}</div>
                  </td>
                  <td style={{ padding: "8px" }}>{row.researcher_email || "—"}</td>
                  <td style={{ padding: "8px" }}><Chip size="small" label={row.study_status} /></td>
                  <td style={{ padding: "8px" }}>
                    <Chip
                      size="small"
                      label={row.health_status}
                      color={
                        row.health_status === "on-track" ? "success" :
                        row.health_status === "monitor" ? "warning" :
                        row.health_status === "needs-attention" ? "error" : "default"
                      }
                      variant="outlined"
                    />
                  </td>
                  <td style={{ padding: "8px" }}>{fmt.num(row.invited)}</td>
                  <td style={{ padding: "8px" }}>{fmt.num(row.enrolled)}</td>
                  <td style={{ padding: "8px" }}>{fmt.num(row.active)}</td>
                  <td style={{ padding: "8px" }}>{fmt.date(row.last_activity_at)}</td>
                  <td style={{ padding: "8px" }}>
                    <Button size="small" variant="outlined" onClick={() => onView(row)}>View</Button>
                  </td>
                </tr>
              ))}
              {!studies.length && (
                <tr><td colSpan={9} style={{ padding: "16px", color: "#666" }}>No studies match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </Box>
      </CardContent>
    </Card>
  );
}

export default StudyMgmtDashboard;
